import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Zap, Clock, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { useFormProtection } from '../hooks/useFormProtection';
import HoneypotField from './HoneypotField';
import RecaptchaNotice from './RecaptchaNotice';

const HeroSection = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { honeypotProps, onInteraction, getProtectionPayload } = useFormProtection('waitlist_signup');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const protection = await getProtectionPayload();

      const response = await axios.post('/api/contact/short', {
        email: email,
        source: 'Homepage Hero',
        ...protection
      });

      if (response.data.status === 'success') {
        navigate('/success');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(error.response?.data?.detail || 'There was an error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Mobile Layout */}
        <div className="lg:hidden space-y-6 sm:space-y-8">
          {/* Title and Subtitle */}
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-4 sm:mb-6">
              Tired of slow and outdated{' '}
              <span style={{ color: '#F97316', fontWeight: 'bold' }}>TS Practice CS</span>
              &nbsp;software?
            </h1>
            
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              Practice Dashboard isn't a replacement for Practice CS—it's the perfect add-on to fill in the gaps, giving your firm the speed, flexibility, and quality you've been missing. Keep an eye on critical metrics for your firm, staff, and clients—all in one place.
            </p>
          </div>
          
          {/* Hero Image */}
          <div className="flex justify-center items-center">
            <img
              src="/Billing Worksheet.jpg"
              alt="Billing Worksheet Dashboard"
              className="w-full h-auto max-w-full object-contain rounded-lg shadow-lg"
              style={{ maxHeight: '300px' }}
            />
          </div>
          
          {/* Badges */}
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <div className="flex items-center bg-green-100 text-green-800 px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base">
              <Zap className="w-4 h-4 mr-2" />
              <span className="font-medium">Efficient</span>
            </div>
            <div className="flex items-center bg-blue-100 text-blue-800 px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base">
              <Clock className="w-4 h-4 mr-2" />
              <span className="font-medium">Fast</span>
            </div>
            <div className="flex items-center bg-purple-100 text-purple-800 px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base">
              <TrendingUp className="w-4 h-4 mr-2" />
              <span className="font-medium">Modern</span>
            </div>
          </div>
          
          {/* Quote */}
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
            <p className="text-gray-700 italic text-base sm:text-lg">
              "It's the perfect solution for firms that want to modernize their practice management tool—without the cost of migrating to an entirely new system."
            </p>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <HoneypotField {...honeypotProps} />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={onInteraction}
              placeholder="Enter your email..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[44px]"
              required
              disabled={isSubmitting}
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors min-h-[44px] whitespace-nowrap"
            >
              {isSubmitting ? 'Submitting...' : 'Interested!'}
            </button>
            <RecaptchaNotice />
          </form>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <h1 className="text-5xl xl:text-6xl font-bold text-gray-900 leading-tight">
              Tired of slow and outdated{' '}
              <span style={{ color: '#F97316', fontWeight: 'bold' }}>TS Practice CS</span>
              &nbsp;software?
            </h1>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              Practice Dashboard isn't a replacement for Practice CS—it's the perfect add-on to fill in the gaps, giving your firm the speed, flexibility, and quality you've been missing. Keep an eye on critical metrics for your firm, staff, and clients—all in one place.
            </p>
            
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full">
                <Zap className="w-4 h-4 mr-2" />
                <span className="font-medium">Efficient</span>
              </div>
              <div className="flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 mr-2" />
                <span className="font-medium">Fast</span>
              </div>
              <div className="flex items-center bg-purple-100 text-purple-800 px-4 py-2 rounded-full">
                <TrendingUp className="w-4 h-4 mr-2" />
                <span className="font-medium">Modern</span>
              </div>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 italic text-lg">
                "It's the perfect solution for firms that want to modernize their practice management tool—without the cost of migrating to an entirely new system."
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="flex flex-col gap-2">
              <div className="flex gap-4">
                <HoneypotField {...honeypotProps} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={onInteraction}
                  placeholder="Enter your email..."
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : 'Interested!'}
                </button>
              </div>
              <RecaptchaNotice className="text-left" />
            </form>
          </div>
          
          <div className="flex justify-center items-center">
            <img
              src="/Billing Worksheet.jpg"
              alt="Billing Worksheet Dashboard"
              className="w-full h-auto max-w-full object-contain rounded-lg shadow-lg"
              style={{ maxHeight: '500px' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;